import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  uploadToStorage,
  removeFromStorage,
  SUPPORTED_IMAGE_FORMATS,
  SUPPORTED_VIDEO_FORMATS,
  MAX_IMAGE_SIZE,
  MAX_VIDEO_SIZE,
} from "../utils/fileUpload";
import {
  applyScheduledDaysToCreatives,
  removeScheduledDaysForUrl,
  setScheduledDaysForUrl,
} from "@/utils/creativeScheduleStorage";
import type { Creative } from "../types";

/* -------------------------------------------------------------------------- */
/*                      useLocationSpecificCreativeUpload                     */
/* -------------------------------------------------------------------------- */

/**
 * Manages per-location creative uploads (multiple files per location).
 */
export const useLocationSpecificCreativeUpload = () => {
  const { toast } = useToast();

  // Record<locationName, Creative[]>
  const [locationCreatives, setLocationCreatives] = useState<Record<string, Creative[]>>(() => {
    try {
      const saved = localStorage.getItem("locationCreatives");
      const parsed = (saved ? JSON.parse(saved) : {}) as Record<string, Creative[]>;

      // Merge in any scheduledDays that were written synchronously by URL
      for (const key of Object.keys(parsed)) {
        parsed[key] = applyScheduledDaysToCreatives(parsed[key] || []);
      }

      return parsed;
    } catch {
      return {};
    }
  });

  // Per-location uploading flags (locationName -> boolean)
  const [uploadingMap, setUploadingMap] = useState<Record<string, boolean>>({});

  // Global isUploading convenience (true if ANY location is uploading)
  const isUploading = Object.values(uploadingMap).some(Boolean);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem("locationCreatives", JSON.stringify(locationCreatives));
  }, [locationCreatives]);

  /* ------------------------------ Validation ------------------------------ */

  const validateFile = useCallback(
    (file: File): boolean => {
      const isImage = SUPPORTED_IMAGE_FORMATS.includes(file.type);
      const isVideo = SUPPORTED_VIDEO_FORMATS.includes(file.type) || file.type.startsWith("video/");

      if (!isImage && !isVideo) {
        toast({
          title: "Unsupported file type",
          description: "Please upload an image (JPG, PNG, GIF) or video (MP4, MOV).",
          variant: "destructive",
        });
        return false;
      }

      if (isImage && file.size > MAX_IMAGE_SIZE) {
        toast({
          title: "Image too large",
          description: "Images must be smaller than 10 MB.",
          variant: "destructive",
        });
        return false;
      }

      if (isVideo && file.size > MAX_VIDEO_SIZE) {
        toast({
          title: "Video too large",
          description: "Videos must be smaller than 100 MB.",
          variant: "destructive",
        });
        return false;
      }

      return true;
    },
    [toast],
  );

  /* ------------------------------- Sanitize -------------------------------- */

  const sanitize = (name: string) =>
    name
      .replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g, "")
      .replace(/[^\w.-]/g, "_")
      .replace(/_{2,}/g, "_")
      .replace(/^_+|_+$/g, "") || "creative";

  /* -------------------------------- Upload -------------------------------- */

  const handleLocationFileChange = useCallback(
    async (locationName: string, e: React.ChangeEvent<HTMLInputElement>, day?: number) => {
      const files = Array.from(e.target.files || []);
      // Only use day if explicitly provided and valid; otherwise leave undefined (user must choose)
      const scheduledDay = (day && day > 0) ? day : undefined;
      console.log(`[locationUpload] Processing ${files.length} files for location: ${locationName}, day: ${scheduledDay ?? 'not set'}`);
      if (files.length === 0) return;

      // Validate all files first
      const validFiles = files.filter(file => validateFile(file));
      console.log(`[locationUpload] ${validFiles.length} valid files after validation`);
      if (validFiles.length === 0) return;

      setUploadingMap((prev) => ({ ...prev, [locationName]: true }));

      try {
        const uploadedCreatives: Creative[] = [];
        
        // Upload ALL valid files (not just the first one)
        for (const file of validFiles) {
          // Only embed day into filename if a day was explicitly provided
          const dayPart = scheduledDay ? `-day${scheduledDay}` : '';
          const fileName = `${Date.now()}-${sanitize(locationName)}${dayPart}-${sanitize(file.name)}`;
          const url = await uploadToStorage(file, fileName);
          
          uploadedCreatives.push({
            name: file.name,
            type: file.type,
            size: file.size,
            url,
            locationName,
            // Only set scheduledDays if day was explicitly provided
            scheduledDays: scheduledDay ? [scheduledDay] : [],
          });
        }

        setLocationCreatives((prev) => {
          const existingCreatives = prev[locationName] || [];
          // Append new creatives (don't replace existing ones for this day)
          console.log(`[locationUpload] Adding ${uploadedCreatives.length} creatives for day ${scheduledDay}`);
          return {
            ...prev,
            [locationName]: [...existingCreatives, ...uploadedCreatives],
          };
        });

        toast({ 
          title: "Uploaded", 
          description: `${validFiles.length} file(s) uploaded for Day ${scheduledDay}.` 
        });
      } catch (err) {
        console.error("[locationUpload] Error:", err);
        toast({
          title: "Upload failed",
          description: err instanceof Error ? err.message : "Unknown error – please retry.",
          variant: "destructive",
        });
      } finally {
        setUploadingMap((prev) => ({ ...prev, [locationName]: false }));
        if (e.target) e.target.value = "";
      }
    },
    [toast, validateFile],
  );

  /* -------------------------------- Remove -------------------------------- */

  const removeLocationCreative = useCallback(
    async (locationName: string, index: number) => {
      const arr = locationCreatives[locationName] || [];
      const creative = arr[index];
      if (!creative) return;

      // Keep schedule storage in sync
      if (creative.url) removeScheduledDaysForUrl(creative.url);

      setUploadingMap((prev) => ({ ...prev, [locationName]: true }));

      try {
        // Only remove from storage if it's not an external link
        if (creative.url && !creative.isExternalLink) {
          await removeFromStorage(creative.url);
        }

        setLocationCreatives((prev) => {
          const updated = [...(prev[locationName] || [])];
          updated.splice(index, 1);
          return { ...prev, [locationName]: updated };
        });
      } catch (err) {
        console.error("[locationUpload] Remove error:", err);
        toast({
          title: "Remove failed",
          description: err instanceof Error ? err.message : "Could not remove file.",
          variant: "destructive",
        });
      } finally {
        setUploadingMap((prev) => ({ ...prev, [locationName]: false }));
      }
    },
    [locationCreatives, toast],
  );

  /* ------------------------------ URL Submit ------------------------------ */

  const handleLocationUrlSubmit = useCallback(
    (locationName: string, url: string) => {
      // Extract a display name from the URL
      let name = 'External Link';
      try {
        const urlObj = new URL(url);
        // Use hostname as name for clarity
        name = urlObj.hostname.replace('www.', '');
      } catch {
        // Keep default name if URL parsing fails
      }

      setLocationCreatives((prev) => {
        const existingCreatives = prev[locationName] || [];
        return {
          ...prev,
          [locationName]: [
            ...existingCreatives,
            {
              name,
              type: 'link',
              size: 0,
              url,
              locationName,
              isExternalLink: true,
              scheduledDays: [], // Don't default to day 1 - user must choose
            },
          ],
        };
      });

      toast({ title: "Added", description: `Link added successfully.` });
    },
    [toast],
  );

  /* -------------------------- Update Scheduled Days ------------------------- */

  const updateLocationCreativeDays = useCallback(
    (locationName: string, creativeIndex: number, days: number[]) => {
      setLocationCreatives((prev) => {
        const locationArr = prev[locationName];
        if (!locationArr || !locationArr[creativeIndex]) return prev;

        const updated = [...locationArr];
        const url = updated[creativeIndex]?.url;

        updated[creativeIndex] = {
          ...updated[creativeIndex],
          scheduledDays: days,
        };

        // Persist by URL immediately to avoid races with effects
        if (url) setScheduledDaysForUrl(url, days);

        return { ...prev, [locationName]: updated };
      });
    },
    [],
  );

  /* ------------------------------ Return API ------------------------------ */

  return {
    locationCreatives,
    isUploading,
    /** Per-location uploading flags for granular UI feedback */
    uploadingMap,
    handleLocationFileChange,
    removeLocationCreative,
    handleLocationUrlSubmit,
    updateLocationCreativeDays,
  };
};
